// Tournaments → Prizes (contracts/tournaments.md §6, §9; GET /admin/v1/cups/awards): the prize queue across cups.
// Gift cards and other money prizes go pending → claimed (the winner confirms in the app, which opens their
// support thread) → sent (the code goes out in that chat) → done; void takes a prize back and passes it down,
// forfeited ones weren't claimed in time. The API's moves: sent from pending or claimed, done from sent or claimed,
// void from anything but done and granted. "Contact winner" posts the templated support message (in the winner's
// language) and opens the thread. Overdue is the API's: claimed more than 3 days ago and not sent; a pending claim
// past its deadline is shown too (the cups job forfeits it). Granted prizes (XP, badges, Pro days) load on their tab.

import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { type Award, useAwards, useContactWinner, useUpdateAward } from "../../api/cups";
import { useAuth, useCan } from "../../auth/AuthContext";
import { tableStyles } from "../../components/table";
import { Button, Card, Chip, Dialog, EmptyState, ErrorState, Field, InlineError, Loading, PageHeader, Segmented } from "../../components/ui";
import { AWARD_STATUSES, AWARD_TONE, type AwardStatus, flag, MONEY_KINDS, PRIZE_KIND_LABEL } from "../../lib/cups";
import { formatDateTime } from "../../lib/dates";
import { shortId } from "../../lib/format";
import { timeAgo } from "../../lib/messaging";
import styles from "../pages.module.css";
import c from "./cups.module.css";

type Filter = "todo" | "all" | AwardStatus;
type Move = "sent" | "done" | "void";

const GIFT_LABEL: Record<string, string> = { amazon: "Amazon", google_play: "Google Play" };

/** Why an award needs attention now, or null: the API's `overdue`, or a claim window that has passed. */
export function overdue(a: Award, now = Date.now()): string | null {
  if (a.overdue) return `claimed ${timeAgo(a.claimedAt ?? a.claim?.at ?? a.createdAt, now)}, code not sent`;
  if (a.status === "pending" && a.claimBy && Date.parse(a.claimBy) < now) return `claim window ended ${formatDateTime(a.claimBy)}: forfeits on the next run`;
  return null;
}

/** The moves the API allows from a status. */
export const MOVES: Record<Move, readonly AwardStatus[]> = { sent: ["pending", "claimed"], done: ["sent", "claimed"], void: ["pending", "claimed", "sent", "forfeited"] };

const isTodo = (a: Award) => a.status === "pending" || a.status === "claimed";
const who = (a: Award) => (a.handle ? `@${a.handle}` : a.userId ? shortId(a.userId) : "a deleted player");

export function PrizesPage() {
  const canEdit = useCan("cups.edit");
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("todo");
  const money = useAwards();
  const granted = useAwards("granted", filter === "granted");
  const awards = filter === "granted" ? granted : money;
  const contact = useContactWinner();
  const [moving, setMoving] = useState<{ award: Award; to: Move } | null>(null);
  const [contacting, setContacting] = useState<string | null>(null);

  const items = awards.data?.items ?? [];
  const moneyItems = money.data?.items ?? [];
  const count = (f: Filter) => (f === "granted" ? (granted.data ? String(granted.data.items.length) : "…") : String(moneyItems.filter((a) => (f === "all" ? true : f === "todo" ? isTodo(a) : a.status === f)).length));
  const shown = items
    .filter((a) => (filter === "all" ? true : filter === "todo" ? isTodo(a) : a.status === filter))
    .sort((a, b) => Number(Boolean(overdue(b))) - Number(Boolean(overdue(a))) || b.createdAt.localeCompare(a.createdAt) || a.rank - b.rank);
  const late = moneyItems.filter((a) => overdue(a)).length;

  const onContact = (a: Award) => {
    setContacting(a.id);
    contact.mutate(a.id, { onSuccess: (r) => navigate(`/support/${r.userId}`), onSettled: () => setContacting(null) });
  };

  return (
    <>
      <PageHeader
        title="Prizes"
        meta={
          canEdit ? (
            <span>
              Gift-card codes go out in the winner's support chat, then the prize is marked sent.
              {late ? <span className={c.overdueText} data-testid="overdue-count">{` ${late} overdue.`}</span> : null}
            </span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · working the prize queue needs the editor role (you are {admin?.role})
            </span>
          )
        }
      />
      <div className={c.filters}>
        <Segmented
          label="Status"
          value={filter}
          onChange={setFilter}
          options={(["todo", ...AWARD_STATUSES.filter((s) => s !== "granted"), "granted", "all"] as const).map((f) => ({
            value: f,
            label: `${f === "todo" ? "To do" : f === "all" ? "All" : f[0]!.toUpperCase() + f.slice(1)} · ${count(f)}`,
          }))}
        />
      </div>
      <InlineError error={contact.error} />
      <Card padded={false}>
        {awards.isPending ? (
          <Loading rows={6} />
        ) : awards.isError ? (
          <ErrorState error={awards.error} onRetry={() => void awards.refetch()} />
        ) : shown.length === 0 ? (
          <EmptyState title={filter === "todo" ? "Nothing to do" : "No prizes here"}>
            {filter === "todo" ? "Every gift card is sent, or waits for a cup's results to be confirmed." : "Nothing with this status."}
          </EmptyState>
        ) : (
          <div className={tableStyles.wrap}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>Winner</th>
                  <th>Prize</th>
                  <th>Status · claim</th>
                  {canEdit ? <th aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {shown.map((a) => {
                  const late = overdue(a);
                  const money = MONEY_KINDS.has(a.kind);
                  return (
                    <tr key={a.id} className={late ? c.overdue : undefined} data-testid="award-row" data-status={a.status} data-overdue={late ? "" : undefined}>
                      <td className={c.wrapCell}>
                        {a.userId ? (
                          <Link to={`/players/${a.userId}`} className={c.player}>
                            {who(a)}
                          </Link>
                        ) : (
                          <span className={c.dim}>{who(a)}</span>
                        )}
                        <span className={c.when}>
                          #{a.rank} {a.bracket} · <Link to={`/tournaments/${a.cupId}`}>{a.cupTitle}</Link>
                        </span>
                      </td>
                      <td className={c.wrapCell}>
                        {a.label}
                        <span className={c.when}>
                          <Chip tone={money ? "warning" : "muted"}>{PRIZE_KIND_LABEL[a.kind]}</Chip> <span className={c.mono}>{a.value}</span>
                          {/* The prize's country board (the API's award.country), not the winner's storefront. */}
                          {a.country ? ` · ${flag(a.country)} ${a.country} board` : ""}
                        </span>
                      </td>
                      <td className={c.wrapCell}>
                        <Chip tone={AWARD_TONE[a.status]}>{a.status}</Chip>
                        {late ? (
                          <span className={`${c.when} ${c.overdueText}`} data-testid="overdue">
                            {late}
                          </span>
                        ) : a.status === "pending" ? (
                          <span className={c.when}>{a.claimBy ? `claim by ${formatDateTime(a.claimBy)}` : a.confirmed ? "claimable" : "waits for the results to be confirmed"}</span>
                        ) : a.doneAt || a.sentAt || a.claimedAt ? (
                          <span className={c.when}>
                            {a.doneAt ? `done ${timeAgo(a.doneAt)}` : a.sentAt ? `sent ${timeAgo(a.sentAt)}` : `claimed ${timeAgo(a.claimedAt!)}`}
                          </span>
                        ) : (
                          <span className={c.when}>since {formatDateTime(a.createdAt)}</span>
                        )}
                        {a.claim ? (
                          <span className={c.claim} data-testid="claim">
                            {a.claim.adult ? "18+ or permission" : <span className={c.overdueText}>not confirmed 18+</span>} · {flag(a.claim.country)} {a.claim.country}
                            {a.country && a.country !== a.claim.country ? <span className={c.overdueText}> (a {a.country} board prize)</span> : null}
                            {a.claim.giftCard ? ` · ${GIFT_LABEL[a.claim.giftCard] ?? a.claim.giftCard}` : ""} · claimed {formatDateTime(a.claim.at)}
                          </span>
                        ) : money && a.status !== "pending" ? null : !money ? (
                          <span className={c.when}>automatic</span>
                        ) : null}
                        {a.adminNote ? <span className={c.note}>“{a.adminNote}”</span> : null}
                      </td>
                      {canEdit ? (
                        <td>
                          <div className={c.actions}>
                            {a.userId && a.status === "pending" && a.confirmed ? (
                              <Button size="sm" variant="secondary" busy={contacting === a.id} onClick={() => onContact(a)} data-testid="contact">
                                Contact winner
                              </Button>
                            ) : null}
                            {a.userId && (a.status === "claimed" || a.status === "sent") ? (
                              <Button size="sm" variant="ghost" onClick={() => navigate(`/support/${a.userId}`)}>
                                Open chat
                              </Button>
                            ) : null}
                            {money && MOVES.sent.includes(a.status) && (a.status === "claimed" || a.confirmed) ? (
                              <Button size="sm" variant={a.status === "claimed" ? "secondary" : "ghost"} onClick={() => setMoving({ award: a, to: "sent" })} data-testid="mark-sent">
                                Mark sent…
                              </Button>
                            ) : null}
                            {money && MOVES.done.includes(a.status) ? (
                              <Button size="sm" variant={a.status === "sent" ? "secondary" : "ghost"} onClick={() => setMoving({ award: a, to: "done" })} data-testid="mark-done">
                                Mark done…
                              </Button>
                            ) : null}
                            {MOVES.void.includes(a.status) ? (
                              <Button size="sm" variant="ghost" onClick={() => setMoving({ award: a, to: "void" })} data-testid="mark-void">
                                Void…
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {moving ? <MoveDialog award={moving.award} to={moving.to} onClose={() => setMoving(null)} /> : null}
    </>
  );
}

const MOVE_TITLE: Record<Move, string> = { sent: "Mark sent", done: "Mark done", void: "Void this prize" };
const MOVE_TEXT: Record<Move, string> = {
  sent: "The gift-card code went out in the winner's support chat.",
  done: "The winner has the prize and nothing is left to do.",
  void: "The winner was disqualified or isn't eligible: the prize passes down to the next eligible entrant.",
};

function MoveDialog({ award, to, onClose }: { award: Award; to: Move; onClose: () => void }) {
  const update = useUpdateAward();
  const [note, setNote] = useState(award.adminNote ?? "");
  const trimmed = note.trim();
  const needsNote = to === "void";
  return (
    <Dialog
      open
      title={`${MOVE_TITLE[to]}: ${award.label} for ${who(award)}?`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            variant={to === "void" ? "danger" : "primary"}
            busy={update.isPending}
            disabled={(needsNote && !trimmed) || trimmed.length > 500}
            onClick={() => update.mutate({ id: award.id, patch: { status: to, adminNote: trimmed || null } }, { onSuccess: onClose })}
            data-testid="move-confirm"
          >
            {MOVE_TITLE[to]}
          </Button>
        </>
      }
    >
      <p className={styles.prose}>{MOVE_TEXT[to]}</p>
      <Field label={needsNote ? "Why · required" : "Note · optional"} hint={`${trimmed.length}/500 · for the team, never shown to the player`}>
        <textarea className={`${styles.textarea} ${c.textarea}`} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} data-testid="move-note" />
      </Field>
      <InlineError error={update.error} />
    </Dialog>
  );
}
